/**
 * Compress an image File to fit under a target byte size using HTML5 Canvas.
 * - Downscales to max 1600px on the long edge (preserves aspect ratio).
 * - Recompresses as JPEG with adaptive quality (starts 0.85, drops if needed).
 * - Returns a new File (always .jpg) ready to upload.
 *
 * Designed for receipt / package-with-resi photos — receipts need to stay
 * legible, so we keep quality reasonably high.
 *
 * Usage:
 *   const compressed = await compressImage(file, { maxBytes: 2 * 1024 * 1024 });
 *
 * @param {File}   file
 * @param {object} opts
 * @param {number} opts.maxBytes      target byte ceiling (default 2 MB)
 * @param {number} opts.maxDimension  max long-edge in px (default 1600)
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  const { maxBytes = 2 * 1024 * 1024, maxDimension = 1600 } = opts;

  if (!file.type.startsWith('image/')) {
    throw new Error('Selected file is not an image.');
  }

  // Already small enough → skip work
  if (file.size <= maxBytes) return file;

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Failed to read file'));
    r.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Failed to decode image'));
    im.src = dataUrl;
  });

  // Compute target dimensions
  let { width, height } = img;
  const long = Math.max(width, height);
  if (long > maxDimension) {
    const scale = maxDimension / long;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Step the JPEG quality down until under target — 0.85 → 0.55 in 5 steps.
  const qualities = [0.85, 0.75, 0.65, 0.6, 0.55];
  let lastBlob = null;
  for (const q of qualities) {
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', q));
    if (!blob) throw new Error('Image encoding failed');
    lastBlob = blob;
    if (blob.size <= maxBytes) break;
  }

  const newName = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
  return new File([lastBlob], newName, { type: 'image/jpeg', lastModified: Date.now() });
}
